import { useMutation, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'

import { makeSelectContentTypesByModel } from '@webapp/core/stores/global'
import { permissionRepository } from '@webapp/platform/iam/data-access/repositories/PermissionRepository'
import {
  ReportAccessGrant,
  type ReportAccessGrantConstructor,
} from '@webapp/platform/iam/entities/ReportAccessGrant'
import { reportAccessGrantQueryConfig } from '@webapp/platform/iam/query-configs/reportAccessGrant'

export const useMutateGrantReportAccess = () => {
  const queryClient = useQueryClient()
  const secondaryCategoryContentType = useSelector(
    makeSelectContentTypesByModel('procurify', 'department')
  )

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: ({
      userId,
      secondaryCategoryId,
    }: {
      userId: number
      secondaryCategoryId: number
    }) =>
      permissionRepository.grantReportAccess({
        userId,
        secondaryCategoryContentTypeId: secondaryCategoryContentType.id,
        secondaryCategoryId,
      }),
    onSuccess: async (grant, { secondaryCategoryId }) => {
      // Dedup-insert at the top of the grants list (BHV-013/BHV-014) on the
      // FULL key — partial keys never match the live cache entry (ADR-5)
      const { queryKey } = reportAccessGrantQueryConfig.list({
        secondaryCategoryId,
        secondaryCategoryContentTypeId: secondaryCategoryContentType.id,
      })

      await queryClient.cancelQueries(queryKey)

      // Refetch instead of seeding when there is no cached list (still
      // loading, failed, or the user switched SecondaryCategory meanwhile) or
      // the cancel reverted an invalidated one: seeding would clear the
      // invalidation and keep stale grants (e.g. after Clear all or a bulk
      // command). An inactive query is only marked stale and fetches on its
      // next mount
      const listState = queryClient.getQueryState(queryKey)
      if (!listState?.data || listState.isInvalidated) {
        queryClient.invalidateQueries(queryKey)
        return
      }

      queryClient.setQueryData<ReportAccessGrantConstructor[]>(
        queryKey,
        (existing = []) =>
          existing.some((existingGrant) => existingGrant.id === grant.id)
            ? existing
            : [grant, ...existing]
      )
    },
  })

  const grantReportAccess = async (
    variables: Parameters<typeof mutateAsync>[0]
  ): Promise<ReportAccessGrant> =>
    new ReportAccessGrant(await mutateAsync(variables))

  return {
    grantReportAccess,
    isGranting: isLoading,
  }
}
